/**
 * Constantes y helpers puros del report de Reposición. Sin estado de
 * React — sólo cálculos y configuración compartida.
 */

// Sin paginación: enviamos un límite alto y mostramos todas las filas.
// Si algún día llegamos a >5000 familias BASICO (improbable), habrá que
// volver a paginar o virtualizar.
export const PAGE_LIMIT = 5000;

// Dimensiones por las que se puede filtrar (mismo set que Eficacia).
export const BASE_FILTERS = {
  genders: { name: 'Género', slug: 'genders', bundle: true },
  categories: { name: 'Categorías', slug: 'categories', bundle: true },
  subcategories: { name: 'Subcategorías', slug: 'subcategories', bundle: true },
};

// Convención de zona civil mexicana (offset fijo -06:00). Las fechas
// del informe se envían como ISO con offset explícito para evitar
// ambigüedades en el back.
const TZ_OFFSET = '-06:00';

const toIsoStartOfDay = (date) => {
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}T00:00:00${TZ_OFFSET}`;
};

// Última campaña iniciada según la fecha actual:
//   Mar–Ago → VERANO Y      [1 mar Y, 1 sep Y)
//   Sep–Dic → INVIERNO Y    [1 sep Y, 1 mar Y+1)
//   Ene–Feb → INVIERNO Y-1  [1 sep Y-1, 1 mar Y)
const getCurrentSeasonRange = () => {
  const now = new Date();
  const m = now.getMonth() + 1;
  const y = now.getFullYear();
  if (m >= 3 && m <= 8) {
    return { from: new Date(y, 2, 1), toExclusive: new Date(y, 8, 1) };
  }
  if (m >= 9) {
    return { from: new Date(y, 8, 1), toExclusive: new Date(y + 1, 2, 1) };
  }
  return { from: new Date(y - 1, 8, 1), toExclusive: new Date(y, 2, 1) };
};

const getPreviousSeasonRange = () => {
  const cur = getCurrentSeasonRange();
  // Mismo length, justo anterior.
  const lengthMs = cur.toExclusive.getTime() - cur.from.getTime();
  return {
    from: new Date(cur.from.getTime() - lengthMs),
    toExclusive: cur.from,
  };
};

const rollingDays = (days) => {
  const now = new Date();
  const toExclusive = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1); // mañana 00:00
  const from = new Date(toExclusive.getTime() - days * 24 * 60 * 60 * 1000);
  return { from, toExclusive };
};

const yearToDate = () => {
  const now = new Date();
  return {
    from: new Date(now.getFullYear(), 0, 1),
    toExclusive: new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1),
  };
};

const lastYear = () => {
  const now = new Date();
  return {
    from: new Date(now.getFullYear() - 1, 0, 1),
    toExclusive: new Date(now.getFullYear(), 0, 1),
  };
};

/**
 * Presets disponibles. Cada uno resuelve a `{ from, toExclusive }` con
 * fechas en zona civil MX (sin componente horario en `from`, 00:00 en
 * `toExclusive`). El `custom` no resuelve — significa "el usuario elige
 * en el DateRangePicker".
 */
export const PERIOD_PRESETS = [
  { id: 'campana-actual', label: 'Campaña actual', range: getCurrentSeasonRange },
  { id: 'campana-anterior', label: 'Campaña anterior', range: getPreviousSeasonRange },
  { id: 'anio-actual', label: 'Año actual', range: yearToDate },
  { id: 'anio-anterior', label: 'Año anterior', range: lastYear },
  { id: 'ultimos-365', label: 'Últimos 365 días', range: () => rollingDays(365) },
  { id: 'ultimos-180', label: 'Últimos 180 días', range: () => rollingDays(180) },
  { id: 'ultimos-90', label: 'Últimos 90 días', range: () => rollingDays(90) },
  { id: 'custom', label: 'Personalizado', range: null },
];

/** Serializa una Date a ISO con offset MX (para mandar al back). */
export const toApiDate = (date) => (date ? toIsoStartOfDay(date) : null);

/** Etiqueta humana del rango (para chips, subtítulos, etc.). */
export const formatRangeLabel = (from, toExclusive) => {
  if (!from || !toExclusive) return '';
  const fmt = (d) =>
    new Intl.DateTimeFormat('es-ES', { day: '2-digit', month: 'short', year: 'numeric' }).format(d);
  // `toExclusive` representa "el día siguiente al final" — para la
  // etiqueta humana restamos un día y mostramos rango inclusivo.
  const lastDay = new Date(toExclusive.getTime() - 24 * 60 * 60 * 1000);
  return `${fmt(from)} – ${fmt(lastDay)}`;
};

// Mediana de un array de números. Null si está vacío. (Igual que en
// efficacy/constants — reusamos para que la UI quede consistente.)
export const median = (arr) => {
  if (!arr || arr.length === 0) return null;
  const sorted = [...arr].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
};

// Bandas del ratio de reposición (acordadas con el usuario).
// REVISAR: si las series caen casi todas fuera de las bandas verdes,
// ensanchar a 0.85–1.15 / 0.6–1.4 etc.
export const REPLENISHMENT_RATIO_BANDS = {
  greenLow: 0.9,
  greenHigh: 1.1,
  amberLow: 0.7,
  amberHigh: 1.3,
};

// Umbrales para clasificar productos en hot (riesgo stockout) y zombi
// (capital muerto). REVISAR: los valores son la primera estimación
// del usuario; afinar después de ver el primer informe real.
export const HOT_THRESHOLDS = {
  coverageMax: 30, // días
  rotationMin: 4, // anual
};

export const ZOMBIE_THRESHOLDS = {
  coverageMin: 180, // días
  rotationMax: 1, // anual
  daysSinceLastSale: 90,
};

/** Devuelve 'green' | 'amber' | 'red' según el ratio. `null` si no aplica. */
export const ratioBand = (ratio) => {
  if (ratio == null) return null;
  const { greenLow, greenHigh, amberLow, amberHigh } = REPLENISHMENT_RATIO_BANDS;
  if (ratio >= greenLow && ratio <= greenHigh) return 'green';
  if (ratio >= amberLow && ratio <= amberHigh) return 'amber';
  return 'red';
};

/** Clasifica una fila como 'hot' | 'zombie' | null. */
export const classifyStockHealth = (row) => {
  const coverage = row.coverage;
  const rotation = row.rotation;
  const daysSinceLastSale = row.daysSinceLastSale;
  if (
    coverage != null &&
    rotation != null &&
    coverage < HOT_THRESHOLDS.coverageMax &&
    rotation > HOT_THRESHOLDS.rotationMin
  ) {
    return 'hot';
  }
  const isOversupplied =
    coverage != null && rotation != null && coverage > ZOMBIE_THRESHOLDS.coverageMin && rotation < ZOMBIE_THRESHOLDS.rotationMax;
  const isStale = daysSinceLastSale != null && daysSinceLastSale > ZOMBIE_THRESHOLDS.daysSinceLastSale;
  if (isOversupplied || isStale) return 'zombie';
  return null;
};
